import type { ProductImageKind } from '../models/productImage'

/**
 * A catalogue name usually carries several of these words at once — "HP OMEN 27q
 * Monitor" is a display, not a laptop; "HP ENVY Inspire 7920e All-in-One Printer"
 * is a printer, not a notebook. So the rules are ordered and the first hit wins:
 * the narrower the family, the earlier it sits.
 *
 * Accessories come last on purpose. A bundle named after its main product
 * ("EliteBook 840 with Dock") should show the laptop, and a standalone accessory
 * carries no device word to be caught by an earlier rule anyway.
 *
 * Word boundaries matter: `\bdisplay\b` must not fire on "DisplayPort cable".
 */
const RULES: ReadonlyArray<readonly [ProductImageKind, RegExp]> = [
  [
    'headset',
    /\b(poly|polycom|plantronics|voyager|blackwire|savi|encorepro|headsets?|headphones?|earbuds?|speakerphone|video bar)\b/i,
  ],
  ['display', /\b(monitors?|display)\b/i],
  [
    'printerMfp',
    /\b(mfp|multifunction|multi-function|laserjet (enterprise|managed)|pagewide|scanjet)\b/i,
  ],
  [
    'printer',
    /\b(printer|laserjet|deskjet|officejet|smart tank|ink tank|neverstop|designjet|latex|indigo|tango)\b/i,
  ],
  [
    'desktop',
    /\b(elitedesk|prodesk|proone|eliteone|workstations?|desktop|all-in-one pc|mini pc|tower|z[2468])\b/i,
  ],
  [
    'laptop',
    /\b(elitebook|probook|zbook|dragonfly|spectre|pavilion|omen|victus|chromebook|notebook|laptop|fortis|x360|envy)\b/i,
  ],
  [
    'accessory',
    /\b(mouse|keyboard|dock|docking|adapter|cable|webcam|camera|backpack|case|sleeve|stylus|pen|charger|battery|care pack|hub|bag)\b/i,
  ],
]

/**
 * Picks the fallback drawing for a product from its catalogue name. An empty or
 * unrecognised name resolves to `generic`.
 */
export const resolveProductImageKind = (productName?: string | null): ProductImageKind => {
  if (!productName) {
    return 'generic'
  }

  return RULES.find(([, pattern]) => pattern.test(productName))?.[0] ?? 'generic'
}
